import MenuItem from "@mui/material/MenuItem";

import { useNavigation } from "@/providers";
import { RobotoStyledPopoverMenu } from "@/shared/components/RobotoStyledPopoverMenu";
import {
  FileSystemNode,
  selectedDirectoriesToPatterns,
  selectedItemsToFileIds,
} from "@/shared/domain/files/filesystem";
import { AddIcon, PlaylistAddIcon } from "@/shared/foundation/icons/mui";

import styles from "./SessionsSubmenu.module.css";

interface SessionsSubmenuProps {
  datasetId: string;
  /**
   * The file-browser selection. Files prefill a session by id; directories become
   * recursive `dir/**` session patterns.
   */
  selectedItems: FileSystemNode[];
  /**
   * The parent selection menu's Paper element. The fly-out anchors to it so the
   * two menus align edge-to-edge and match height.
   */
  anchorEl: HTMLElement | null;
  open: boolean;
  /** Close just the fly-out. */
  onClose: () => void;
  /** Close the entire selection menu stack (fly-out + parent menu). */
  onCloseMenu: () => void;
  /**
   * Opens the add-to-existing-session flow, which the dataset operation bar hosts. Undefined
   * where nothing hosts it, and the menu then offers only "Create new".
   */
  onAddToSession: (() => void) | undefined;
}

/**
 * Sessions fly-out for the selection menu, for a dataset-scoped file
 * selection: "Create new" opens the create-session page seeded with the
 * selection, and "Add to existing" calls `onAddToSession` when one is given.
 */
export function SessionsSubmenu({
  datasetId,
  selectedItems,
  anchorEl,
  open,
  onClose,
  onCloseMenu,
  onAddToSession,
}: SessionsSubmenuProps) {
  const { goto } = useNavigation();

  return (
    <RobotoStyledPopoverMenu
      id="sessions-submenu"
      MenuListProps={{
        "aria-labelledby": "sessions-submenu-button",
      }}
      slotProps={{
        paper: {
          className: styles.submenuPaper,
          // Only the height is runtime-computed: match the parent menu's
          // Paper so the two panels' top and bottom edges align.
          style: { height: anchorEl?.offsetHeight },
        },
      }}
      anchorEl={anchorEl}
      open={open && anchorEl !== null}
      onClose={onClose}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
    >
      <MenuItem
        disableRipple
        onClick={() => {
          goto.createSession({
            datasetId,
            fileIds: selectedItemsToFileIds(selectedItems),
            directoryPatterns: selectedDirectoriesToPatterns(selectedItems),
          });
        }}
      >
        <AddIcon /> Create new
      </MenuItem>
      <AddToExistingAction
        onAddToSession={onAddToSession}
        onCloseMenu={onCloseMenu}
      />
    </RobotoStyledPopoverMenu>
  );
}

function AddToExistingAction({
  onAddToSession,
  onCloseMenu,
}: {
  onAddToSession: (() => void) | undefined;
  onCloseMenu: () => void;
}) {
  if (!onAddToSession) {
    return null;
  }
  return (
    <MenuItem
      disableRipple
      onClick={() => {
        onAddToSession();
        onCloseMenu();
      }}
    >
      <PlaylistAddIcon /> Add to existing
    </MenuItem>
  );
}
